import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import 'dayjs/locale/ar';
import 'dayjs/locale/en';
import 'dayjs/locale/zh-cn';
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

const monthLocaleForDayjs = () => {
  const locale = dayjs.locale();
  if (locale === 'ar') return 'ar';
  if (locale === 'zh-cn') return 'zh-CN';
  return 'en-US';
};

export const accountingPeriodLocalesToTry = (monthYearString) => {
  if (/[\u0600-\u06FF]/.test(monthYearString)) return ['ar', 'zh-cn', 'en'];
  if (/[\u4e00-\u9fff]/.test(monthYearString)) return ['zh-cn', 'en', 'ar'];
  return ['en', 'ar', 'zh-cn'];
};

export const GULF_TIMEZONE = 'Asia/Dubai';

export function nowInGulfTimeString() {
  return dayjs().tz(GULF_TIMEZONE).format('HH:mm:ss');
}

export function formatDate(date) {
  if (!date) return 'N/A';

  try {
    const parsed = dayjs(date);
    return parsed.isValid() ? parsed.format('DD-MM-YYYY') : 'N/A';
  } catch {
    return 'N/A';
  }
}

export function formatDateWithTimestamp(date) {
  if (!date) return 'N/A';

  try {
    const parsed = dayjs(date);
    return parsed.isValid() ? parsed.format('DD-MM-YYYY hh:mm:ss A') : 'N/A';
  } catch {
    return 'N/A';
  }
}

export const formatToUAEDateTime = (timestamp) => {
  if (!timestamp) return 'N/A';

  try {
    const uaeTime = dayjs.utc(timestamp).tz('Asia/Dubai');
    return uaeTime.isValid() ? `${uaeTime.format('MMM DD, YYYY, hh:mm:ss A')} GST` : 'N/A';
  } catch {
    return 'N/A';
  }
};

export const formatToIsoDate = (date) => {
  if (!date) return 'N/A';

  try {
    const parsed = dayjs(date);
    return parsed.isValid() ? parsed.toISOString().split('T')[0] : 'N/A';
  } catch {
    return 'N/A';
  }
};

export const formatDateRangeLabel = ({ start, end }) => {
  const startDate = new Date(start);
  const endDate = new Date(end);

  const year = startDate.getFullYear();
  const monthLocale = monthLocaleForDayjs();
  const month = startDate.toLocaleString(monthLocale, { month: 'short' });
  const startDay = String(startDate.getDate()).padStart(2, '0');
  const endDay = String(endDate.getDate()).padStart(2, '0');

  return `${startDay}-${endDay} ${month.toUpperCase()}, ${year}`;
};

export const formatDateToDashboardFormat = (inputDate) => {
  const date = new Date(inputDate);
  const monthLocale = monthLocaleForDayjs();
  const month = date.toLocaleString(monthLocale, { month: 'short' });
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();

  return `${day} ${month.toUpperCase()}, ${year} `;
};

export const parseAccountingPeriodValue = (monthYearString) => {
  if (!monthYearString) return null;
  if (dayjs.isDayjs(monthYearString)) {
    return monthYearString.isValid() ? monthYearString.startOf('month') : null;
  }

  // Parse "January 2024", Arabic month-year, or Simplified Chinese "一月 2024".
  // Try every locale so a value stored in a previous UI language still parses.
  const localesToTry = accountingPeriodLocalesToTry(monthYearString);

  for (const loc of localesToTry) {
    const parsed = dayjs(monthYearString, 'MMMM YYYY', loc, true);
    if (parsed.isValid()) return parsed.startOf('month');
  }

  const fallback = dayjs(monthYearString);
  return fallback.isValid() ? fallback.startOf('month') : null;
};

export const convertMonthYearToDate = (monthYearString, isStartDate) => {
  const parsed = parseAccountingPeriodValue(monthYearString);
  if (!parsed) return null;

  return isStartDate ? parsed.format('YYYY-MM-DD') : parsed.endOf('month').format('YYYY-MM-DD');
};
